import { PaginationProps } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '../../../../app/hooks';
import { useGetScansQuery } from '../../../../services/entities/impl/blfApi/endpoints/scans';
import { Scan } from '../../../../services/entities/impl/blfApi/schemas/scan';
import {
  selectScansBlockID,
  selectScansCustomerID,
  selectScansRanchID,
  selectSearchValue,
} from '../../scansSlice';

export const useRecentScansPaginated = (
  pageSize: number = DEFAULT_SCAN_LIST_PAGE_SIZE,
  refreshInterval?: number // fetch new data every x milliseconds
): {
  page: number;
  totalPages: number;
  handlePageChange: NonNullable<PaginationProps['onChange']>;
  displayScans: Scan[];
  /**
   * Indicates that the query is loading for the first time
   */
  isLoading: boolean;
  /**
   * Indicates that the query is fetching a new page
   */
  isFetchingPage: boolean;
} => {
  const [currentPage, setCurrentPage] = useState(1);

  const customerID = useAppSelector(selectScansCustomerID);
  const ranchID = useAppSelector(selectScansRanchID);
  const blockID = useAppSelector(selectScansBlockID);
  const searchValue = useAppSelector(selectSearchValue);

  // Reset currentPage to 1 when customer, ranch, or block changes
  useEffect(() => {
    setCurrentPage(1);
  }, [customerID, ranchID, blockID]);

  // Get the current items to display
  const { currentData, isLoading, isFetching, data } = useGetScansQuery(
    {
      customer__id__in:
        customerID && customerID !== 'ALL_CUSTOMERS' ? [customerID] : undefined,
      ranch__id__in:
        ranchID && ranchID !== 'ALL_RANCHES' ? [ranchID] : undefined,
      block__id__in:
        blockID && blockID !== 'ALL_BLOCKS' ? [blockID] : undefined,
      page: currentPage,
      size: pageSize,
      search: searchValue ? searchValue : undefined,
      order_by: '-scan_date',
    },
    refreshInterval
      ? {
          pollingInterval: refreshInterval,
          refetchOnMountOrArgChange: refreshInterval,
        }
      : {}
  );

  const { items: displayScans, pages } = currentData ??
    data ?? {
      items: [],
      page: 1,
      pages: 1,
    };

  // Handle page change request
  const handlePageChange = useCallback<
    NonNullable<PaginationProps['onChange']>
  >((_event, value) => {
    setCurrentPage(value);
  }, []);

  return {
    totalPages: pages,
    page: currentPage,
    handlePageChange,
    displayScans,
    isLoading: isLoading,
    isFetchingPage: isFetching,
  };
};

export const DEFAULT_SCAN_LIST_PAGE_SIZE = 100 as const;
